/**
 * atc-button.js — <atc-button> custom element.
 *
 * A styled add-to-cart button that:
 *  - on click, triggers the section's product form add-to-cart (reusing Horizon's
 *    flow: variant, quantity, selling_plan, cart drawer, cross-sell). Needs a
 *    "Buy buttons" block present in the layout.
 *  - keeps its "<label> – <price>" + struck compare reactive to pack changes and to
 *    subscription mode. Prices come from a Liquid-baked map (per variant one-time /
 *    compare / subscription). Subscribe vs one-time is read from the form's
 *    selling_plan input — the single source of truth for what will be added.
 */
if (!customElements.get('atc-button')) {
  customElements.define(
    'atc-button',
    class AtcButton extends HTMLElement {
      connectedCallback() {
        this.section = this.closest('.shopify-section') || document;
        this.form = this.section.querySelector('form[data-type="add-to-cart-form"]');
        this.btn = this.querySelector('.atc-btn__btn');
        this.priceEl = this.querySelector('.atc-btn__price');
        this.compareEl = this.querySelector('.atc-btn__compare');
        this.data = this.readPrices();

        this.onClick = this.onClick.bind(this);
        if (this.btn) this.btn.addEventListener('click', this.onClick);

        if (this.data) {
          this.render = this.render.bind(this);
          this.section.addEventListener('shopify:product:select', this.render);
          this.section.addEventListener('change', this.render);
          if (this.form) {
            this.formObs = new MutationObserver(this.render);
            this.formObs.observe(this.form, {
              childList: true,
              subtree: true,
              attributes: true,
              attributeFilter: ['value'],
            });
          }
          this.render();
        }
      }

      disconnectedCallback() {
        if (this.btn) this.btn.removeEventListener('click', this.onClick);
        this.section.removeEventListener('shopify:product:select', this.render);
        this.section.removeEventListener('change', this.render);
        if (this.formObs) this.formObs.disconnect();
      }

      readPrices() {
        const el = this.querySelector('script[data-atc-prices]');
        if (!el) return null;
        try {
          return JSON.parse(el.textContent);
        } catch (e) {
          console.warn('[atc-button] bad price map', e);
          return null;
        }
      }

      getVariantId() {
        const checked = this.section.querySelector('.variant-cards__input:checked');
        if (checked && checked.dataset.variantId) return String(checked.dataset.variantId);
        const input = this.form && this.form.querySelector('input[name="id"]');
        let id = input && input.value;
        if (!id) id = new URLSearchParams(window.location.search).get('variant');
        return id ? String(id) : Object.keys(this.data.variants)[0];
      }

      subscribeActive() {
        const input = this.form && this.form.querySelector('input[name="selling_plan"]');
        return !!(input && input.value);
      }

      render() {
        const entry = this.data.variants[this.getVariantId()];
        if (!entry) return;

        const subscribe = this.subscribeActive() && entry.sub;
        // subscribe → sub price, strike the one-time price.
        // one-time → variant price, strike the compare-at price (only if on sale).
        const price = subscribe ? entry.sub : entry.one;
        const compare = subscribe ? entry.one : entry.compare;

        if (this.priceEl) this.setText(this.priceEl, price);
        if (this.compareEl) {
          if (compare) {
            this.setText(this.compareEl, compare);
            this.compareEl.hidden = false;
          } else {
            this.compareEl.hidden = true;
          }
        }
      }

      onClick(event) {
        event.preventDefault();
        const form = this.form || this.section.querySelector('form[data-type="add-to-cart-form"]');
        if (!form) {
          console.warn('[atc-button] No add-to-cart form found — the "Buy buttons" block must be present (enabled) in this section.');
          return;
        }
        const add = form.querySelector('[ref="addToCartButton"], button[type="submit"], [name="add"]');
        if (add && add.disabled) return; // sold out / unavailable
        // requestSubmit fires the form's submit handler (Horizon's AJAX add + cart drawer)
        // and works even when the native block is visually hidden.
        if (form.requestSubmit) form.requestSubmit(add || undefined);
        else if (add) add.click();
        else form.submit();
      }

      setText(el, value) {
        if (el && el.textContent !== value) el.textContent = value;
      }
    }
  );
}
